import { useEffect, useMemo, useCallback, useState } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';
import Typography from '@mui/material/Typography';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PageLoader } from '@/components/shared/PageLoader';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { TemplateForm } from '../TemplateForm';
import { CopyTemplateDialog } from './CopyTemplateDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { FormTypes } from '../constants';
import { selectedTemplateId, selectedWorklogsTemplateId } from '../state';
import {
  AllLogsTemplatesDocument,
  GetVersionsDocument,
  useAddLogsTemplateMutation,
  useAllLogsTemplatesQuery,
  useCreateLogsTemplateMutation,
  useCopyLogsTemplateMutation,
  useSyncLogsTemplatesMutation,
  LogsTemplateDocument,
  LogTemplateType,
  LogCategory,
} from '@/__generated__/graphql';
import { removeTypename, useSnackBarMessage } from '../utils';
import { FieldPanel } from '../TemplateForm/styles';

type WorklogsTemplateProps = {
  structureId: string;
};

export const WorklogsTemplate = ({ structureId }: WorklogsTemplateProps) => {
  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const setWorklogsTemplateId = useSetAtom(selectedWorklogsTemplateId);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState<boolean>(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data: allLogsTemplates, loading: isLoading } = useAllLogsTemplatesQuery({
    variables: {
      input: {
        structureId,
        type: LogTemplateType.WorklogTemplate,
      },
    },
    skip: !structureId || !selectedAssemblyTemplateId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (allLogsTemplates?.allLogsTemplates) {
      setWorklogsTemplateId(allLogsTemplates?.allLogsTemplates.at(0)?.id);
    }
  }, [allLogsTemplates?.allLogsTemplates, setWorklogsTemplateId]);

  const workpackTemplate = useMemo(() => {
    return removeTypename(allLogsTemplates?.allLogsTemplates?.at(0));
  }, [allLogsTemplates?.allLogsTemplates]);

  const downloadJson = useCallback(() => {
    const jsonData = JSON.stringify(workpackTemplate, undefined, 2);
    const jsonBlob = new Blob([jsonData], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(jsonBlob);
    const fileName = `${
      workpackTemplate?.name ?? 'Workpack Template'
    }-${Date.now().toString()}.json`;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
  }, [workpackTemplate]);

  const [createNewTemplate] = useCreateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [addWorkpackTemplate] = useAddLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [copyWorkpackTemplate] = useCopyLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const [syncWorkpackTemplates, { loading: isSyncing }] = useSyncLogsTemplatesMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument, GetVersionsDocument],
  });

  const addNewTemplate = useCallback(() => {
    createNewTemplate({
      variables: {
        input: {
          structureId,
          type: LogTemplateType.WorklogTemplate,
        },
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsOpen(false);
      },
    }).then((response) => {
      addWorkpackTemplate({
        variables: {
          input: {
            logsTemplateId: response?.data?.createLogsTemplate?.id ?? '',
            structureId,
            category: LogCategory.Worklog,
          },
        },
        onCompleted: (response) => {
          if (response?.addLogsTemplate) {
            showSnackBar('Worklogs Template created successfully', 'success');
            setIsOpen(false);
          }
        },
        onError: (errorResponse) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsOpen(false);
        },
      });
    });
  }, [createNewTemplate, addWorkpackTemplate, structureId, showSnackBar]);

  const copyTemplate = useCallback(() => {
    setIsCopyDialogOpen(true);
  }, []);

  const handleCopyTemplate = useCallback(
    (sourceStructureId: string, sourceTemplateId: string) => {
      copyWorkpackTemplate({
        variables: {
          input: {
            sourceStructureId,
            sourceTemplateId,
            targetStructureId: structureId,
          },
        },
        onCompleted: (response) => {
          if (response?.copyLogsTemplate) {
            showSnackBar('Worklogs template copied successfully', 'success');
            setIsCopyDialogOpen(false);
          }
        },
        onError: (errorResponse) => {
          console.error(errorResponse);
          showSnackBar(`${errorResponse.message}`, 'error');
          setIsCopyDialogOpen(false);
        },
      });
    },
    [copyWorkpackTemplate, structureId, showSnackBar]
  );

  const handleSyncTemplates = useCallback(() => {
    if (!workpackTemplate?.id) {
      showSnackBar('No template selected for sync', 'error');
      return;
    }

    syncWorkpackTemplates({
      variables: {
        input: {
          structureId,
          templateId: workpackTemplate.id,
        },
      },
      onCompleted: (response) => {
        if (response?.syncLogsTemplates) {
          showSnackBar('Worklogs templates synced successfully', 'success');
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
      },
    });
  }, [syncWorkpackTemplates, structureId, workpackTemplate?.id, showSnackBar]);

  return (
    <Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          pt: 4,
          alignItems: 'center',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          {workpackTemplate && !isLoading ? (
            <Stack direction="row" spacing={1}>
              <Tooltip title="This will sync linked structures" arrow placement="top">
                <Button
                  startIcon={
                    isSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                  }
                  variant="contained"
                  onClick={handleSyncTemplates}
                  sx={containedButtonStyle}
                  disabled={isSyncing}
                >
                  Sync Templates
                </Button>
              </Tooltip>
              <Button
                startIcon={<DownloadIcon />}
                variant="contained"
                onClick={() => downloadJson()}
                sx={containedButtonStyle}
              >
                Download JSON
              </Button>
            </Stack>
          ) : (
            <>
              <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={() => setIsOpen(true)}
              >
                Add New Worklogs Template
              </Button>
              <Button
                startIcon={<ContentCopyIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={copyTemplate}
              >
                Copy Worklogs Template
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      {isLoading && <PageLoader />}
      {workpackTemplate && (
        <TemplateForm currentTemplateType={FormTypes.Logs.toString()} structureId={structureId} />
      )}
      {!workpackTemplate && !isLoading && (
        <Box sx={{ pt: 2 }}>
          <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
            <EmptyStatePlaceholder type="template" message="No Templates Found" />
          </FieldPanel>
        </Box>
      )}
      {isOpen && (
        <GenericDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title="Create new template"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to create a new worklogs template?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsOpen(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={addNewTemplate} sx={containedButtonStyle}>
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      <CopyTemplateDialog
        open={isCopyDialogOpen}
        onClose={() => setIsCopyDialogOpen(false)}
        onCopy={handleCopyTemplate}
        currentStructureId={structureId}
      />
    </Stack>
  );
};
